import React from 'react';
import { Link } from 'react-router-dom';
import { PublicPage } from '../support/elements';
import BrandMark from '../common/BrandMark';

function PrivacyPolicy() {
    return (
        <PublicPage
            width="max-w-4xl"
            footer={
                <div className="flex flex-col items-center gap-3">
                    <Link to="/support" className="inline-flex items-center gap-1.5 font-medium text-primary-600 hover:text-primary-700">
                        ← Volver a Soporte
                    </Link>
                    <p className="m-0 text-surface-400 text-xs">© 2025 Anais Isabel Turbec</p>
                </div>
            }>
            <div className="mb-8 pb-8 border-b border-surface-100 flex flex-col items-center text-center">
                <div className="mb-4">
                    <BrandMark size={56} />
                </div>
                <h1 className="m-0 text-[28px] sm:text-3xl font-semibold tracking-tight text-surface-900">Política de Privacidad</h1>
                <p className="m-0 mt-1.5 text-surface-500">Shelf - Auditoría de Tiendas</p>
                <p className="m-0 mt-3 inline-flex items-center gap-1.5 rounded-md bg-surface-100 px-2 h-6 text-xs font-medium text-surface-600">
                    Última actualización: 29 de octubre de 2025
                </p>
            </div>

            <div className="max-w-none text-[15px] text-surface-700 space-y-8">
                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">1. Introducción</h2>
                    <p className="m-0 leading-relaxed">
                        Shelf ("nosotros", "nuestro" o "la aplicación") es una plataforma de auditoría de retail diseñada para uso empresarial. Esta
                        Política de Privacidad explica cómo recopilamos, usamos y protegemos su información cuando utiliza nuestra aplicación móvil.
                    </p>
                    <p className="m-0 mt-3 leading-relaxed">
                        Esta aplicación está diseñada para empleados autorizados de organizaciones que utilizan la plataforma Shelf. Al usar la aplicación,
                        usted acepta las prácticas descritas en esta política.
                    </p>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">2. Información que Recopilamos</h2>

                    <h3 className="m-0 mt-5 mb-2 text-[15px] font-semibold text-surface-800">2.1 Información de Cuenta</h3>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>Nombre completo</li>
                        <li>Dirección de correo electrónico</li>
                        <li>Número de teléfono</li>
                        <li>Rol organizacional (Asesor, Promotor, Gerente)</li>
                        <li>Organización/Cliente asignado</li>
                    </ul>

                    <h3 className="m-0 mt-5 mb-2 text-[15px] font-semibold text-surface-800">2.2 Datos de Ubicación</h3>
                    <p className="m-0 leading-relaxed">Recopilamos datos de ubicación GPS en tiempo real cuando:</p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>Registra una visita a una tienda</li>
                        <li>Realiza una auditoría de tienda</li>
                        <li>Documenta inventario en el campo</li>
                    </ul>
                    <p className="m-0 mt-2 leading-relaxed">
                        <strong className="font-semibold text-surface-900">Propósito:</strong> Verificar la ubicación de visitas a tiendas para reportes de cumplimiento y gestión de auditorías.
                    </p>

                    <h3 className="m-0 mt-5 mb-2 text-[15px] font-semibold text-surface-800">2.3 Fotos y Multimedia</h3>
                    <p className="m-0 leading-relaxed">La aplicación accede a su cámara y almacenamiento para:</p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>Capturar fotografías de productos durante auditorías</li>
                        <li>Documentar configuraciones de tiendas</li>
                        <li>Registrar evidencia de inventario</li>
                    </ul>
                    <p className="m-0 mt-2 leading-relaxed">
                        Las fotos se almacenan localmente en su dispositivo y se sincronizan con nuestros servidores seguros para reportes empresariales.
                    </p>

                    <h3 className="m-0 mt-5 mb-2 text-[15px] font-semibold text-surface-800">2.4 Datos de Auditoría</h3>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>Información de tiendas visitadas</li>
                        <li>Disponibilidad de productos</li>
                        <li>Niveles de inventario</li>
                        <li>Marcas de tiempo de auditorías</li>
                        <li>Notas y observaciones</li>
                    </ul>

                    <h3 className="m-0 mt-5 mb-2 text-[15px] font-semibold text-surface-800">2.5 Información del Dispositivo</h3>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>Tipo y modelo del dispositivo</li>
                        <li>Versión del sistema operativo</li>
                        <li>Identificadores únicos del dispositivo</li>
                        <li>Información de red (tipo de conexión, proveedor)</li>
                    </ul>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">3. Cómo Usamos su Información</h2>
                    <p className="m-0 leading-relaxed">Utilizamos la información recopilada para:</p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>
                            <strong className="font-semibold text-surface-900">Gestión de Auditorías:</strong> Procesar y almacenar datos de auditorías de tiendas
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Verificación de Ubicación:</strong> Confirmar visitas a tiendas para reportes de cumplimiento
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Reportes Empresariales:</strong> Generar análisis e informes para su organización
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Sincronización de Datos:</strong> Habilitar funcionalidad offline y sincronización de datos
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Comunicación:</strong> Enviar notificaciones relacionadas con el trabajo (alertas de inventario, asignaciones de tareas)
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Soporte y Mantenimiento:</strong> Proporcionar asistencia técnica y mejorar el rendimiento de la aplicación
                        </li>
                    </ul>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">4. Compartir Datos</h2>
                    <p className="m-0 leading-relaxed">Sus datos se comparten dentro de su organización con:</p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>
                            <strong className="font-semibold text-surface-900">Gerentes y Supervisores:</strong> Acceso a reportes de auditoría y datos de desempeño
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Miembros del Equipo:</strong> Información relevante para colaboración en tareas
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Administradores del Sistema:</strong> Acceso completo para gestión de usuarios y datos
                        </li>
                    </ul>
                    <p className="m-0 mt-4 leading-relaxed">
                        <strong className="font-semibold text-surface-900">No vendemos, alquilamos ni compartimos sus datos personales con terceros para fines de marketing.</strong>
                    </p>
                    <p className="m-0 mt-2 leading-relaxed">Podemos compartir datos con:</p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>
                            <strong className="font-semibold text-surface-900">Proveedores de Servicios:</strong> Proveedores de hosting, almacenamiento en la nube y servicios de análisis bajo
                            acuerdos estrictos de confidencialidad
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Requisitos Legales:</strong> Si lo exige la ley o para proteger nuestros derechos legales
                        </li>
                    </ul>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">5. Seguridad de Datos</h2>
                    <p className="m-0 leading-relaxed">Implementamos medidas de seguridad estándar de la industria:</p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>
                            <strong className="font-semibold text-surface-900">Cifrado:</strong> Todas las transmisiones de datos usan cifrado HTTPS/TLS
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Almacenamiento Seguro:</strong> Los datos en reposo se almacenan en servidores seguros
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Control de Acceso:</strong> Autenticación basada en roles y control de acceso
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Auditorías Regulares:</strong> Revisiones de seguridad y actualizaciones periódicas
                        </li>
                    </ul>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">6. Retención de Datos</h2>
                    <p className="m-0 leading-relaxed">Retenemos sus datos personales mientras:</p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>Su cuenta esté activa</li>
                        <li>Sea necesario para proporcionar servicios a su organización</li>
                        <li>Lo requieran obligaciones legales o contractuales</li>
                    </ul>
                    <p className="m-0 mt-4 leading-relaxed">
                        Cuando su empleo termina o su cuenta se desactiva, sus datos personales pueden ser retenidos por su organización según sus políticas
                        internas y requisitos legales.
                    </p>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">7. Sus Derechos</h2>
                    <p className="m-0 leading-relaxed">Usted tiene derecho a:</p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>
                            <strong className="font-semibold text-surface-900">Acceso:</strong> Solicitar una copia de sus datos personales
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Corrección:</strong> Solicitar correcciones a información inexacta
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Eliminación:</strong> Solicitar la eliminación de sus datos (sujeto a requisitos de retención de su organización)
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Portabilidad:</strong> Recibir sus datos en un formato estructurado y de uso común
                        </li>
                        <li>
                            <strong className="font-semibold text-surface-900">Objeción:</strong> Objetar ciertos procesamientos de sus datos
                        </li>
                    </ul>
                    <p className="m-0 mt-4 leading-relaxed">
                        Para ejercer estos derechos, contacte al administrador de su organización o envíenos un correo a{' '}
                        <a href="mailto:info@shelfve.com" className="font-medium text-primary-600 hover:underline">
                            info@shelfve.com
                        </a>
                    </p>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">8. Aplicación para Uso Empresarial</h2>
                    <p className="m-0 leading-relaxed">Shelf es una herramienta empresarial proporcionada por su empleador. Su organización:</p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>Controla el acceso a la aplicación</li>
                        <li>Gestiona las cuentas de usuario</li>
                        <li>Posee los datos generados a través de la aplicación</li>
                        <li>Establece políticas de retención de datos</li>
                    </ul>
                    <p className="m-0 mt-4 leading-relaxed">Consulte también las políticas de privacidad y uso de datos de su empleador.</p>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">9. Privacidad de Menores</h2>
                    <p className="m-0 mt-3 leading-relaxed">
                        Esta aplicación está diseñada para uso profesional por empleados autorizados. No recopilamos intencionalmente información de menores
                        de 18 años. Si un menor ha proporcionado información personal, contacte a{' '}
                        <a href="mailto:info@shelfve.com" className="font-medium text-primary-600 hover:underline">
                            info@shelfve.com
                        </a>{' '}
                        para su eliminación.
                    </p>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">10. Cambios a esta Política</h2>
                    <p className="m-0 leading-relaxed">
                        Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos sobre cambios significativos a través de:
                    </p>
                    <ul className="my-2 pl-5 list-disc space-y-1.5 marker:text-surface-300">
                        <li>Notificaciones en la aplicación</li>
                        <li>Correo electrónico</li>
                        <li>Publicación de la política actualizada en nuestro sitio web</li>
                    </ul>
                    <p className="m-0 mt-4 leading-relaxed">
                        La fecha de "Última actualización" en la parte superior indica cuándo se realizó la revisión más reciente.
                    </p>
                </section>

                <section>
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">11. Información de Contacto</h2>
                    <p className="m-0 leading-relaxed">Para preguntas o inquietudes sobre esta Política de Privacidad o nuestras prácticas de datos:</p>
                    <div className="mt-4 rounded-xl border border-surface-200 bg-surface-50 p-4">
                        <p className="m-0 font-semibold text-surface-900">Shelf - Auditoría de Tiendas</p>
                        <p className="m-0 mt-2 text-surface-700">
                            Email:{' '}
                            <a href="mailto:info@shelfve.com" className="font-medium text-primary-600 hover:underline">
                                info@shelfve.com
                            </a>
                        </p>
                        <p className="m-0 mt-1 text-surface-700">
                            Web:{' '}
                            <a href="https://shelfve.com" target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline">
                                www.shelfve.com
                            </a>
                        </p>
                    </div>
                </section>

                <section className="border-t border-surface-100 pt-8">
                    <h2 className="m-0 mb-3 text-lg font-semibold tracking-tight text-surface-900">12. Consentimiento</h2>
                    <p className="m-0 leading-relaxed">
                        Al usar la aplicación Shelf, usted reconoce que ha leído y comprendido esta Política de Privacidad y acepta la recopilación, uso y
                        divulgación de su información según se describe aquí.
                    </p>
                </section>
            </div>
        </PublicPage>
    );
}

export default PrivacyPolicy;
